import { Component } from '@angular/core';
import { ToggleCustomEvent, ViewDidLeave, ViewWillEnter } from '@ionic/angular';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { InventoryService } from '@inventory/services/inventory.service';
import { Observable } from 'rxjs';
import {
  InventoryCardEllipsisClick,
  InventoryPageData,
} from '@inventory/models/inventory-view.model';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { NavigationHelperService } from 'src/app/shared/helpers/navigation.helpers.service';
import { PATHS } from '@shared/constants/paths';
import { AllInventoryService } from './services/all-inventory.service';
import { InventoryOptions } from '@usf/ngrx-inventory';
import { SortableSectionHeader } from '../../../shared/components/sortable-section/models/sortable-section.model';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-all-inventory',
  templateUrl: './all-inventory.page.html',
  styleUrls: ['./all-inventory.page.scss'],
})
export class AllInventoryPage implements ViewWillEnter, ViewDidLeave {
  // observables
  allInventoriesPageData$: Observable<InventoryPageData>;
  inventoryOptions$: Observable<InventoryOptions>;
  breadcrumbs: Crumb[];
  // search
  searchKey = '';

  constructor(
    readonly allInventoryService: AllInventoryService,
    readonly inventoryService: InventoryService,
    readonly platformService: PlatformService,
    private translateService: TranslateService,
    private panAppState: PanAppState,
    private navigationHelperService: NavigationHelperService,
  ) {}

  ionViewWillEnter(): void {
    this.searchKey = '';
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.allInventories),
    );

    this.allInventoriesPageData$ = this.allInventoryService.allInventoriesView$(
      this.platformService.platformType,
    );

    this.inventoryOptions$ = this.allInventoryService.selectInventoryOptions$();
    this.allInventoryService.trackAllInventoriesPageLoaded();

    var homeCrumbUrl = this.platformService.isTouch.value
      ? '/touch/home'
      : '/desktop/home';

    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: homeCrumbUrl,
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.inventory',
        url: '/inventory',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.allInventories',
        url: '',
        active: true,
      },
    ];
  }

  search() {
    this.allInventoryService.searchInAllInventories(this.searchKey);
  }

  goToInventoryWorksheet(path: string) {
    this.navigationHelperService.routeTo(path);
  }

  updateSort(sortableSectionHeader: SortableSectionHeader) {
    this.allInventoryService.updateAllInventoriesSort(
      sortableSectionHeader?.headerName,
      sortableSectionHeader?.sortDirection,
    );
  }

  openInventoryCardEllipsis(
    data: InventoryCardEllipsisClick,
    inventoryPageData: InventoryPageData,
  ) {
    this.inventoryService.openInventoryCardEllipsis(data, inventoryPageData);
  }

  goToCreateInventory() {
    this.navigationHelperService.routeTo(
      PATHS.INVENTORY.concat('/', PATHS.CREATE_INVENTORY),
    );
  }

  copyInventory(inventoryId: string) {
    this.inventoryService.setInventoryToCopy(inventoryId);
    this.navigationHelperService.routeTo(
      PATHS.INVENTORY.concat('/', PATHS.CREATE_INVENTORY),
    );
  }

  toggleShowCompleted(event: Event) {
    const toggleEvent = event as ToggleCustomEvent;
    this.allInventoryService.toggleShowCompleted(toggleEvent.detail.checked);
  }

  ionViewDidLeave(): void {
    this.allInventoryService.resetAllInventoriesSort();
    this.allInventoryService.searchInAllInventories('');
  }
}
